import { companyData } from '@/mocks/company';
import { defaultTemplateConfig, replaceYears } from '@/mocks/template-config';
import { findPresetsByCategory } from '@/mocks/template-presets';
import type { ProposalMock } from '@/mocks/proposals';
import type { TemplateConfig } from '@/mocks/template-config';

interface PDFCoverProps {
  proposal: ProposalMock;
  config?: TemplateConfig;
}

const DEFAULT_COVER_IMAGE = 'https://readdy.ai/api/search-image?query=Modern%20luxury%20house%20at%20dusk%20with%20white%20security%20surveillance%20camera%20mounted%20on%20exterior%20wall%20in%20foreground%2C%20warm%20golden%20ambient%20lighting%20from%20house%20windows%2C%20dark%20navy%20blue%20evening%20sky%2C%20professional%20security%20system%20installation%20scene%2C%20elegant%20contemporary%20architecture%20with%20glass%20and%20concrete%2C%20subtle%20landscape%20garden%20lighting%2C%20premium%20residential%20security%20atmosphere%2C%20photorealistic%20architectural%20photography%20style%2C%20dark%20moody%20tones%20with%20golden%20warm%20light%20accents%2C%20high%20contrast%20between%20camera%20and%20house%20background%2C%20clean%20professional%20composition%20suitable%20for%20commercial%20proposal%20cover&width=1400&height=900&seq=cover-bg-2026&orientation=landscape';

function getCoverImageByCategory(category: string): string {
  try {
    const presets = findPresetsByCategory(category as Parameters<typeof findPresetsByCategory>[0]);
    const active = presets.find((p) => p.active);
    return active?.pdfCoverImage || DEFAULT_COVER_IMAGE;
  } catch {
    return DEFAULT_COVER_IMAGE;
  }
}

export { getCoverImageByCategory };

export default function PDFCover({ proposal, config }: PDFCoverProps) {
  const cfg = config || defaultTemplateConfig;
  const institutional = cfg.institutional;
  const visibleDifferentials = institutional.differentials.filter((d) => d.show);
  const activeSpecs = cfg.specialties.filter((s) => s.active).sort((a, b) => a.order - b.order);

  const proposalNumber = proposal.proposal_number || proposal.id;
  const clientCompany = proposal.clientCompany || '';
  const createdAt = proposal.createdAt;

  // Imagem da capa: usa a imagem personalizada se existir, senão usa a da categoria
  const COVER_IMAGE = proposal.cover_image || getCoverImageByCategory(proposal.category || 'cameras');

  const formatDate = (isoString: string | null | undefined) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div
      style={{
        width: '210mm',
        height: '297mm',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Arial, sans-serif",
        boxSizing: 'border-box',
      }}
    >
      {/* ===== TOPO AZUL ESCURO ===== */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '72mm',
          background: 'linear-gradient(180deg, #0a1628 0%, #0f1f35 100%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 20mm',
        }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          {/* Círculo com E estilizado */}
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              border: '2px solid #c9a44a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: '#c9a44a',
                fontSize: '20px',
                fontWeight: 700,
                fontFamily: "Georgia, serif",
                lineHeight: 1,
              }}
            >
              E
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span
              style={{
                color: '#ffffff',
                fontSize: '26px',
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              Eletrônica
            </span>
            <span
              style={{
                color: '#c9a44a',
                fontSize: '26px',
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              2000
            </span>
          </div>
        </div>

        {/* DESDE 1990 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <div style={{ width: '50px', height: '1px', backgroundColor: '#c9a44a' }} />
          <span
            style={{
              color: '#c9a44a',
              fontSize: '11px',
              letterSpacing: '0.35em',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            DESDE {companyData.foundedYear}
          </span>
          <div style={{ width: '50px', height: '1px', backgroundColor: '#c9a44a' }} />
        </div>

        {/* PROPOSTA COMERCIAL */}
        <h1
          style={{
            color: '#ffffff',
            fontSize: '36px',
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontWeight: 400,
            letterSpacing: '0.08em',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.15,
            textTransform: 'uppercase',
          }}
        >
          PROPOSTA
        </h1>
        <h1
          style={{
            color: '#ffffff',
            fontSize: '36px',
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontWeight: 400,
            letterSpacing: '0.08em',
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.15,
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}
        >
          COMERCIAL
        </h1>

        {/* Subtítulo */}
        <p
          style={{
            color: '#c9a44a',
            fontSize: '14px',
            letterSpacing: '0.04em',
            textAlign: 'center',
            margin: 0,
            fontWeight: 400,
          }}
        >
          Solução personalizada para sua necessidade
        </p>
      </div>

      {/* ===== FAIXA CURVA DOURADA ===== */}
      <div
        style={{
          position: 'absolute',
          top: '71mm',
          left: '-10%',
          right: '-10%',
          height: '6mm',
          background: '#0a1628',
          borderRadius: '0 0 50% 50%',
          borderBottom: '2px solid #c9a44a',
          zIndex: 15,
        }}
      />

      {/* ===== IMAGEM DA CASA ===== */}
      <div
        style={{
          position: 'absolute',
          top: '75mm',
          left: 0,
          right: 0,
          height: '95mm',
          zIndex: 5,
          overflow: 'hidden',
        }}
      >
        <img
          src={COVER_IMAGE}
          alt="Capa"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          crossOrigin="anonymous"
        />
        {/* Gradiente suave na parte inferior da imagem para transição */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30mm',
            background: 'linear-gradient(to top, #0a1628 0%, transparent 100%)',
          }}
        />
      </div>

      {/* ===== CAIXA BRANCA DO CLIENTE ===== */}
      <div
        style={{
          position: 'absolute',
          top: '118mm',
          left: '16mm',
          width: '95mm',
          zIndex: 20,
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          padding: '12px 14px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          border: '1px solid rgba(201,164,74,0.3)',
        }}
      >
        {/* Badge PROPOSTA PERSONALIZADA */}
        <div
          style={{
            display: 'inline-block',
            backgroundColor: '#0a1628',
            color: '#c9a44a',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '5px 14px',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        >
          PROPOSTA PERSONALIZADA
        </div>

        <p
          style={{
            color: '#555555',
            fontSize: '11px',
            margin: '0 0 6px 0',
            fontStyle: 'italic',
          }}
        >
          Preparada especialmente para:
        </p>

        <h2
          style={{
            color: '#0a1628',
            fontSize: '22px',
            fontWeight: 700,
            margin: '0 0 8px 0',
            lineHeight: 1.2,
          }}
        >
          {proposal.clientName}
        </h2>

        {/* Linha dourada */}
        <div style={{ width: '100%', height: '2px', backgroundColor: '#c9a44a', marginBottom: '10px' }} />

        {/* Dados */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-user-line" style={{ color: '#0a1628', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#444444', fontSize: '11px' }}>
              Empresa: {clientCompany || '—'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-file-list-3-line" style={{ color: '#0a1628', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#444444', fontSize: '11px' }}>
              Proposta Nº: {proposalNumber}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ri-calendar-line" style={{ color: '#0a1628', fontSize: '12px' }} />
            </div>
            <span style={{ color: '#444444', fontSize: '11px' }}>
              Data de Emissão: {formatDate(createdAt)}
            </span>
          </div>
        </div>
      </div>

      {/* ===== PARTE INFERIOR - AZUL ESCURO ===== */}
      <div
        style={{
          position: 'absolute',
          top: '170mm',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #0a1628 0%, #070f1a 100%)',
          zIndex: 10,
          padding: '0 16mm',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Diferenciais - Título */}
        <div style={{ marginTop: '8mm', marginBottom: '5mm' }}>
          <h3
            style={{
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            {replaceYears(institutional.text, institutional.foundedYear)}
          </h3>
        </div>

        {/* Lista de diferenciais em 2 colunas */}
        {visibleDifferentials.length > 0 && (
          <div style={{ display: 'flex', gap: '8mm', marginBottom: '6mm' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {visibleDifferentials.slice(0, Math.ceil(visibleDifferentials.length / 2)).map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ri-check-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
                  </div>
                  <span style={{ color: '#e8e8e8', fontSize: '10px', lineHeight: 1.3 }}>
                    {replaceYears(item.text, institutional.foundedYear)}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {visibleDifferentials.slice(Math.ceil(visibleDifferentials.length / 2)).map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ri-check-line" style={{ color: '#c9a44a', fontSize: '12px' }} />
                  </div>
                  <span style={{ color: '#e8e8e8', fontSize: '10px', lineHeight: 1.3 }}>
                    {replaceYears(item.text, institutional.foundedYear)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logo marca d'água sutil */}
        <div
          style={{
            position: 'absolute',
            top: '20mm',
            right: '10mm',
            width: '70mm',
            height: '70mm',
            borderRadius: '50%',
            border: '1px solid rgba(201,164,74,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: '50mm',
              height: '50mm',
              borderRadius: '50%',
              border: '1px solid rgba(201,164,74,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: '30mm',
                height: '30mm',
                borderRadius: '50%',
                backgroundColor: 'rgba(201,164,74,0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: 'rgba(201,164,74,0.08)', fontSize: '40px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
                E
              </span>
            </div>
          </div>
        </div>

        {/* RODAPÉ */}
        <div style={{ marginTop: 'auto', paddingBottom: '4mm' }}>
          {/* Linha separadora */}
          <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(201,164,74,0.2)', marginBottom: '4mm' }} />

          <div style={{ display: 'flex', gap: '4mm', alignItems: 'flex-start' }}>
            {/* QR Code */}
            <div style={{ width: '22mm', flexShrink: 0, textAlign: 'center' }}>
              <img
                src={cfg.footer.qrCode.image}
                alt="QR Code"
                style={{ width: '20mm', height: '20mm', borderRadius: '3px', objectFit: 'cover' }}
                crossOrigin="anonymous"
              />
              <p style={{ color: '#9ca3af', fontSize: '7px', margin: '2px 0 0 0', lineHeight: 1.3 }}>
                Escaneie para falar conosco pelo WhatsApp
              </p>
            </div>

            {/* Contatos */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-whatsapp-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>{companyData.phone1}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-whatsapp-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>{companyData.phone2}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-mail-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>{companyData.email}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-global-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>{companyData.website}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-map-pin-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>
                  {companyData.city} – {companyData.state}
                </span>
              </div>
            </div>

            {/* Horário */}
            <div style={{ width: '40mm', flexShrink: 0 }}>
              <p style={{ color: '#c9a44a', fontSize: '9px', fontWeight: 700, margin: '0 0 3px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                HORÁRIO DE ATENDIMENTO
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-time-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>Seg-Sex: 8h às 18h</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-time-line" style={{ color: '#c9a44a', fontSize: '10px' }} />
                </div>
                <span style={{ color: '#e8e8e8', fontSize: '9px' }}>Sábado: 8h às 12h</span>
              </div>
              <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(201,164,74,0.15)', margin: '3px 0' }} />
              <span style={{ color: '#e8e8e8', fontSize: '8px' }}>
                CNPJ: {companyData.cnpj}
              </span>
            </div>

            {/* Logo + slogan */}
            <div style={{ width: '35mm', flexShrink: 0, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '3px' }}>
                <div
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '1.5px solid #c9a44a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: '#c9a44a', fontSize: '10px', fontWeight: 700, fontFamily: 'Georgia, serif' }}>
                    E
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{ color: '#ffffff', fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>Eletrônica</span>
                  <span style={{ color: '#c9a44a', fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>2000</span>
                </div>
              </div>
              <p style={{ color: '#c9a44a', fontSize: '7px', margin: 0, letterSpacing: '0.05em', lineHeight: 1.3 }}>
                SEGURANÇA • AUTOMAÇÃO<br />• CONFIANÇA
              </p>
            </div>
          </div>

          {/* Especialidades */}
          {activeSpecs.length > 0 && (
            <div style={{ marginTop: '3mm' }}>
              <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(201,164,74,0.15)', marginBottom: '3mm' }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '5mm' }}>
                {activeSpecs.map((spec, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <div style={{ width: '12px', height: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`${spec.icon}`} style={{ color: '#c9a44a', fontSize: '10px' }} />
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      {spec.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}